import type { ReactElement } from 'react';
import { ArrowLeft } from 'lucide-react';

import { Container } from '~/components/ui/Container';
import { useContent, useLocale } from '~/hooks/useContent';
import { localeToPath } from '~/i18n/config';
import { cn } from '~/lib/cn';

type LegalPageKey = 'privacy' | 'terms';

interface LegalPageProps {
  pageKey: LegalPageKey;
}

/**
 * Shared renderer for the Privacy Policy and Terms of Use pages.
 *
 * Pulls content for the current locale from the page context and renders a
 * readable, legal-document-style layout:
 *
 *   ← Home link
 *   H1 title
 *   Last-updated line (in Steel Mist)
 *   Intro paragraph
 *   <section> per content entry: H2 heading + body paragraph
 *
 * No hero, no scroll animations — readability is king for legal text.
 * Layout already provides `<main id="main">`; this component renders a
 * semantic `<article>` as its root so there is no landmark collision.
 */
export function LegalPage({ pageKey }: LegalPageProps): ReactElement {
  const content = useContent();
  const locale = useLocale();
  const page = content[pageKey];

  // Localised "Back to home" label — we don't want yet another content key
  // for a single string, so we key off the locale here.
  const backLabel =
    locale === 'pl'
      ? 'Powrót do strony głównej'
      : locale === 'de'
        ? 'Zur Startseite'
        : 'Back to home';

  return (
    <article
      className={cn(
        'pt-32 md:pt-40 pb-24 md:pb-32',
        'bg-[var(--color-void-navy)] text-[var(--color-frost-white)]'
      )}
    >
      <Container>
        <div className="mx-auto max-w-[720px]">
          {/* Back-to-home link */}
          <a
            href={localeToPath(locale)}
            className={cn(
              'inline-flex items-center gap-2 mb-10',
              'text-sm font-display font-semibold tracking-[0.18em] uppercase',
              'text-[var(--color-steel-mist)] hover:text-[var(--color-cyan-signal)]',
              'transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cyan-signal)] rounded-sm'
            )}
          >
            <ArrowLeft size={14} strokeWidth={2} aria-hidden="true" />
            <span>{backLabel}</span>
          </a>

          {/* Title */}
          <h1
            className={cn(
              'font-display font-extrabold tracking-tight uppercase leading-[0.95]',
              'text-4xl md:text-5xl',
              'text-[var(--color-frost-white)] mb-3'
            )}
          >
            {page.title}
          </h1>

          {/* Last-updated */}
          <p
            className={cn(
              'text-sm font-display font-semibold tracking-[0.14em] uppercase',
              'text-[var(--color-steel-mist)] mb-8'
            )}
          >
            {page.lastUpdated}
          </p>

          {/* Intro */}
          <p
            className={cn(
              'text-base font-body font-light leading-[1.8]',
              'text-[var(--color-frost-white)]/85 mb-12'
            )}
          >
            {page.intro}
          </p>

          {/* Sections */}
          <div className="flex flex-col gap-10">
            {page.sections.map((section) => (
              <section key={section.heading} className="flex flex-col gap-3">
                <h2
                  className={cn(
                    'font-display font-bold text-2xl tracking-[0.03em]',
                    'text-[var(--color-frost-white)]'
                  )}
                >
                  {section.heading}
                </h2>
                <p
                  className={cn(
                    'text-base font-body font-light leading-[1.8]',
                    'text-[var(--color-frost-white)]/85'
                  )}
                >
                  {section.body}
                </p>
              </section>
            ))}
          </div>
        </div>
      </Container>
    </article>
  );
}

export default LegalPage;
